import React from 'react';
import useMediaQuery from '@mui/system/useMediaQuery';
import { Grid, Typography, useTheme } from '@mui/joy';
import Dialog from '@mui/material/Dialog';
import Box from '@mui/material/Box';
import { Button, CircularProgress } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Formik } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { NetworkCheckRounded } from '@mui-symbols-material/w300';
import { BasicModalContent } from '@dreebit-registry/vsm-microservice-components';
import { InstalledBaseNode, NetworkScanState } from '../../codegen/graphql';
import NodeModalTitle from '../InstalledBaseTreeColumn/NodeModalTitle';
import InstalledBaseNodePathBreadcrumb from '../InstalledBaseNodePathBreadcrumb';
import { SCAN_DATE_FORMAT } from '../../config/networkScanConfig';
import NetworkScanGuideLink from '../NetworkScanGuide/NetworkScanGuideLink';
import NetworkScanStateChoice from './NetworkScanStateChoice';

export interface NetworkScanFormValues {
    state: NetworkScanState;
    /** Wire format YYYY-MM-DD. */
    scanDate: string;
}

interface Props {
    open: boolean;
    /** The node the scan is documented on — title row and path band, like the create-node modal. */
    node?: InstalledBaseNode | null;
    /** Prefill when editing the node's own scan. */
    initial?: { state: NetworkScanState | string; scanDate: string } | null;
    busy?: boolean;
    onClose: () => void;
    onSubmit: (values: NetworkScanFormValues) => void;
}

const STATES = Object.values(NetworkScanState) as string[];

/**
 * "Document network scan" dialog (IBM-1039): result as traffic light plus the
 * day of the scan. Only these two fields exist on the backend — who documented
 * it lands in the audit log, the filled-in sheet belongs under Documents.
 */
const NetworkScanFormModal = ({ open, node, initial, busy, onClose, onSubmit }: Props) => {
    const theme = useTheme();
    const isFullScreen = useMediaQuery(theme.breakpoints.down('md'));
    const { t } = useTranslation();

    const validationSchema = Yup.object({
        state: Yup.string().oneOf(STATES, t('Please select a result.')).required(t('Please select a result.')),
        scanDate: Yup.string().required(t('Scan date is required.')),
    });

    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} fullScreen={isFullScreen} fullWidth maxWidth="sm">
            <BasicModalContent
                startIcon={<NetworkCheckRounded />}
                onClose={busy ? undefined : onClose}
                style={{ gap: '13px' }}
                title={node
                    ? <NodeModalTitle prefix={initial ? t('Edit network scan') : t('Document network scan')} node={node} labelType="title" />
                    : <Typography level="titleSmallLight">{initial ? t('Edit network scan') : t('Document network scan')}</Typography>}
            >
                {node && (
                    // mt cancels the BasicModalContent gap so the band sits flush, like the create-node modal
                    <Box sx={{ mt: 0.5, mb: 1 }}>
                        <InstalledBaseNodePathBreadcrumb nodeId={node.id} />
                    </Box>
                )}
                <Formik
                    initialValues={{
                        state: (initial?.state as NetworkScanState) ?? ('' as const),
                        scanDate: initial?.scanDate ?? moment().format(SCAN_DATE_FORMAT),
                    }}
                    validationSchema={validationSchema}
                    onSubmit={(values) => onSubmit({ state: values.state as NetworkScanState, scanDate: values.scanDate })}
                >
                    {({ values, errors, touched, setFieldValue, setFieldTouched, handleSubmit }) => (
                        <form
                            data-testid="network-scan-form"
                            onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}
                            // Enter inside the date field must not submit a half-filled form.
                            onKeyDown={(e) => { if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') e.preventDefault(); }}
                        >
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px', pt: '8px' }}>
                                <DatePicker
                                    label={`${t('Scan date')} *`}
                                    format="DD.MM.YYYY"
                                    value={values.scanDate ? moment(values.scanDate, SCAN_DATE_FORMAT) : null}
                                    onChange={(next) => {
                                        setFieldValue('scanDate', next && next.isValid() ? next.format(SCAN_DATE_FORMAT) : '');
                                        setFieldTouched('scanDate', true, false);
                                    }}
                                    disabled={busy}
                                    slotProps={{
                                        textField: {
                                            size: 'small',
                                            fullWidth: true,
                                            error: !!(touched.scanDate && errors.scanDate),
                                            helperText: touched.scanDate && errors.scanDate ? String(errors.scanDate) : undefined,
                                            inputProps: { 'data-testid': 'network-scan-date' },
                                        },
                                    }}
                                />

                                <NetworkScanStateChoice
                                    value={values.state}
                                    onChange={(state) => { setFieldValue('state', state); setFieldTouched('state', true, false); }}
                                    error={touched.state && errors.state ? String(errors.state) : undefined}
                                    disabled={busy}
                                />

                                <NetworkScanGuideLink sx={{ alignSelf: 'flex-start' }} />
                            </Box>

                            <Grid container spacing={1} sx={{ pt: '24px' }}>
                                <Grid xs={6}>
                                    <Button fullWidth variant="outlined" onClick={onClose} disabled={busy}>
                                        {t('Cancel')}
                                    </Button>
                                </Grid>
                                <Grid xs={6}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        type="submit"
                                        disabled={busy}
                                        data-testid="network-scan-save"
                                        startIcon={busy ? <CircularProgress size={16} /> : undefined}
                                    >
                                        {t('Save')}
                                    </Button>
                                </Grid>
                            </Grid>
                        </form>
                    )}
                </Formik>
            </BasicModalContent>
        </Dialog>
    );
};

export default NetworkScanFormModal;
